import { BlockDefinition, BlockDefinitionArray, BlockType } from '@longy/common/cms'

import { CtaButtonRenderer } from './CtaRenderers'
import {
    FullWidthVideoRenderer,
    GalleryRenderer,
    ImageRenderer,
    SpeakerRenderer,
} from './MediaRenderers'
import { byOrder } from './shared'
import { FaqRenderer, QuoteRenderer, TextRenderer } from './TextRenderers'

const renderBlock = (block: BlockDefinition) => {
    switch (block.type) {
        case BlockType.TEXT:
            return <TextRenderer {...block} />
        case BlockType.QUOTE:
            return <QuoteRenderer {...block} />
        case BlockType.FAQ:
            return <FaqRenderer {...block} />
        case BlockType.IMAGE:
            return <ImageRenderer {...block} />
        case BlockType.GALLERY:
            return <GalleryRenderer {...block} />
        case BlockType.FULL_WIDTH_VIDEO:
            return <FullWidthVideoRenderer {...block} />
        case BlockType.SPEAKER:
            return <SpeakerRenderer {...block} />
        case BlockType.CTA_BUTTON:
            return <CtaButtonRenderer {...block} />
        default: {
            // Exhaustive on purpose: a new block type with no arm is a compile error here rather
            // than a block that silently renders nothing on a live page.
            const exhaustive: never = block
            return exhaustive
        }
    }
}

export type BlocksRendererProps = {
    blocks: BlockDefinitionArray
    nested?: boolean
}

export const BlocksRenderer = ({ blocks, nested = false }: BlocksRendererProps) => {
    // The stored array order is not authoritative; `order` is.
    const ordered = byOrder(blocks)

    return (
        <div className="flex grow flex-col">
            {ordered.map((block) => {
                const rendered = renderBlock(block)
                if (!rendered) return null

                return (
                    <div
                        key={block.id}
                        // The dom id is the block id, which is the entire `block:<id>` in-page
                        // anchor mechanism. The scroll offset keeps a sticky header from covering
                        // the target.
                        id={nested ? undefined : block.id}
                        className={nested ? undefined : 'scroll-mt-24'}
                    >
                        {rendered}
                    </div>
                )
            })}
        </div>
    )
}
